import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { validateEmbedToken } from '../../config/embed';

interface EmbedWrapperProps {
  children: React.ReactNode;
}

export type EmbedSize = 'small' | 'medium' | 'large';

export interface EmbedFilters {
  team?: string;
  horizontalTeam?: string;
  month?: string;
  employee?: string;
  refresh?: number;
  size?: EmbedSize;
}

const SCALE_FACTORS: Record<EmbedSize, number> = {
  small: 0.5,
  medium: 1,
  large: 1.5,
};

export function useEmbedFilters(): EmbedFilters {
  const [searchParams] = useSearchParams();
  const sizeParam = searchParams.get('size') as EmbedSize | null;
  return {
    team: searchParams.get('team') || undefined,
    horizontalTeam: searchParams.get('horizontalTeam') || undefined,
    month: searchParams.get('month') || undefined,
    employee: searchParams.get('employee') || undefined,
    refresh: searchParams.get('refresh') ? parseInt(searchParams.get('refresh')!, 10) : undefined,
    size: sizeParam && SCALE_FACTORS[sizeParam] ? sizeParam : undefined,
  };
}

export const EmbedWrapper: React.FC<EmbedWrapperProps> = ({ children }) => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const sizeParam = (searchParams.get('size') as EmbedSize) || 'medium';
  const scale = SCALE_FACTORS[sizeParam] || 1;
  const [state, setState] = useState<'loading' | 'valid' | 'invalid'>('loading');
  const contentRef = useRef<HTMLDivElement>(null);
  const [wrapperHeight, setWrapperHeight] = useState<number | undefined>();

  useEffect(() => {
    let cancelled = false;
    validateEmbedToken(token).then(valid => {
      if (!cancelled) setState(valid ? 'valid' : 'invalid');
    });
    return () => { cancelled = true; };
  }, [token]);

  useEffect(() => {
    if (scale === 1 || !contentRef.current) return;
    const el = contentRef.current;
    const update = () => setWrapperHeight(Math.ceil(el.offsetHeight * scale));
    const observer = new ResizeObserver(update);
    observer.observe(el);
    update();
    return () => observer.disconnect();
  }, [scale]);

  if (state === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3"></div>
          <p className="text-gray-500 text-sm">Validerer tilgang...</p>
        </div>
      </div>
    );
  }

  if (state === 'invalid') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-2">Unauthorized</h1>
          <p className="text-gray-600">Invalid or missing embed token.</p>
        </div>
      </div>
    );
  }

  if (scale === 1) {
    return (
      <div className="bg-white min-h-screen p-4">
        {children}
      </div>
    );
  }

  return (
    <div
      className="bg-white overflow-hidden"
      style={{
        width: `${100 * scale}vw`,
        height: wrapperHeight,
      }}
    >
      <div
        ref={contentRef}
        className="p-4"
        style={{
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
          width: '100vw',
        }}
      >
        {children}
      </div>
    </div>
  );
};

export default EmbedWrapper;
